import { useCallback } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { db } from '../lib/db/indexeddb'

/**
 * A UI preference that follows the user across their devices.
 *
 * Backed by the synced `userPreferences` store (Dexie → Supabase, see the
 * user_preferences migration). Reading goes through a LiveQuery, so a value
 * arriving from another device lands in the UI on its own — that is what makes
 * the sync visible rather than merely present.
 *
 * `ready` is false for the first frame, before Dexie has answered. Render the
 * unfiltered / neutral state during that frame instead of the default value,
 * or the UI will flash the default before showing the stored value.
 *
 * Do NOT use this for theme, mode or language (needed before first paint and
 * before login), window bounds or display mode (device-specific), or sync
 * bookkeeping — those stay in their existing device-local stores.
 *
 * @example
 * const [categoryIds, setCategoryIds, ready] = usePreference('rewardPicker.categoryIds', [])
 */
export function usePreference<T>(
  key: string,
  defaultValue: T,
): [T, (value: T) => void, boolean] {
  // `getPreference` never resolves to undefined — it falls back to the
  // default — so undefined unambiguously means "still loading".
  const stored = useLiveQuery(() => db.getPreference<T>(key, defaultValue), [key])

  const setValue = useCallback((value: T) => {
    // Fire-and-forget: the LiveQuery above delivers the new value back, and a
    // failed local write is not worth blocking the interaction for.
    db.setPreference(key, value)
      .catch(err => console.error(`[usePreference] Failed to store "${key}":`, err))
  }, [key])

  return [stored ?? defaultValue, setValue, stored !== undefined]
}
